/**
 * Event tab form model: loading an event into the form, and turning the
 * form back into a create body or an update patch.
 *
 * An update sends only the fields the operator changed. The form cannot
 * represent every event field faithfully — times are minute-precision,
 * a null type reads as 'Rally' — so re-sending an untouched field can
 * still change it. A changed behavioral field bumps the geofence revision
 * and re-pushes config to every armed phone, which also discards the
 * drivers' local gate edits.
 *
 * `max_stage_seconds` is deliberately absent: the Stages tab owns it as
 * SS1's Safety timeout, and the backend supplies the default on create.
 */

import { EventCreate, EventPatch, EventRow } from '@/services/fleet';
import { eventLocalToUtc, todayInZone, utcToEventLocal } from '@/lib/eventTime';

// Dates and times are wall-clock in `timezone`; the API holds UTC.
export interface FormState {
  name: string;
  type: string;
  startDate: string;
  startTime: string;
  openEnded: boolean;
  endDate: string;
  endTime: string;
  location: string;
  timezone: string;
  description: string;
  public: boolean;
  // Public-directory opt-in (exposes the join code).
  listed: boolean;
  // Self-service flags (default ON).
  allowIndividualSchedule: boolean;
  allowIndividualQuality: boolean;
}

export function emptyForm(now: Date = new Date()): FormState {
  const today = todayInZone('Europe/Tallinn', now);
  return {
    name: '',
    type: 'Rally',
    startDate: today,
    startTime: '08:00',
    openEnded: false,
    endDate: today,
    endTime: '22:00',
    location: '',
    timezone: 'Europe/Tallinn',
    description: '',
    public: true,
    listed: false,
    allowIndividualSchedule: true,
    allowIndividualQuality: true,
  };
}

export function eventToForm(e: EventRow): FormState {
  const openEnded = e.active_until == null;
  const start = utcToEventLocal(e.active_from, e.timezone);
  const end = openEnded
    ? { date: start.date, time: '22:00' }
    : utcToEventLocal(e.active_until!, e.timezone);
  return {
    name: e.name,
    type: e.type || 'Rally',
    startDate: start.date,
    startTime: start.time,
    openEnded,
    endDate: end.date,
    endTime: end.time,
    location: e.location || '',
    timezone: e.timezone || 'UTC',
    description: e.description || '',
    public: e.public,
    listed: e.listed ?? false,
    allowIndividualSchedule: e.allow_individual_schedule ?? true,
    allowIndividualQuality: e.allow_individual_quality ?? true,
  };
}

export function formToCreateBody(form: FormState): EventCreate {
  return {
    name: form.name.trim(),
    type: form.type,
    location: form.location.trim() || null,
    description: form.description.trim() || null,
    timezone: form.timezone,
    active_from: `${form.startDate}T${form.startTime || '08:00'}:00`,
    active_until: form.openEnded
      ? null
      : `${form.endDate}T${form.endTime || '22:00'}:00`,
    public: form.public,
    listed: form.listed,
    allow_individual_schedule: form.allowIndividualSchedule,
    allow_individual_quality: form.allowIndividualQuality,
  };
}

/**
 * The fields whose serialized value differs between the form as it was
 * loaded and as it is now. Diff against the loaded snapshot, not a freshly
 * polled event: a field another operator changed meanwhile would otherwise
 * be reverted to this form's stale value. An explicit `active_until: null`
 * survives the diff — the backend reads it as "make open-ended".
 */
export function formToUpdatePatch(loaded: FormState, edited: FormState): EventPatch {
  const before = formToCreateBody(loaded);
  const after = formToCreateBody(edited);
  const patch: Record<string, unknown> = {};
  for (const key of Object.keys(after) as (keyof EventCreate)[]) {
    if (after[key] !== before[key]) patch[key] = after[key];
  }
  return patch as EventPatch;
}

/** Client-side window sanity — mirrors the backend 400. */
export function windowError(form: FormState): string | null {
  if (form.openEnded) return null;
  const start = `${form.startDate}T${form.startTime || '08:00'}`;
  const end = `${form.endDate}T${form.endTime || '22:00'}`;
  if (end <= start) return 'Event end must be after the start';
  return null;
}

function utcHm(d: Date): string {
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

export function timezoneChangePreview(loaded: FormState, edited: FormState): string | null {
  if (edited.timezone === loaded.timezone) return null;
  const start = edited.startTime || '08:00';
  const oldUtc = eventLocalToUtc(edited.startDate, start, loaded.timezone);
  const newUtc = eventLocalToUtc(edited.startDate, start, edited.timezone);
  const localTimes = edited.openEnded ? start : `${start}–${edited.endTime || '22:00'}`;
  return `Local times stay ${localTimes}; the start moves from ${utcHm(oldUtc)} UTC to ${utcHm(newUtc)} UTC.`;
}
